/**
 * Building blocks of the catalogue editor's forms (Sản phẩm & giá): a field labelled and explained
 * from `catalog:editor.field.<name>` (every field has a plain label and help text in both
 * languages, `tests/catalog-editor.test.mjs`), the form shell every write uses (the reason the
 * server records, the result, the error), checkbox lists and the code input.
 */
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Callout, Field } from '../../components/blocks';
import { WriteError } from '../../components/Write';
import type { catalog as en } from '../../i18n/locales/en/catalog';
import { suggestCode } from '../../lib/catalog';
import { FormFooter, ReasonField } from '../operator/shared';

export type FieldName = keyof (typeof en)['editor']['field'];

export function useCatalogT() {
  const { t, i18n } = useTranslation('catalog');
  const isEn = i18n.language.startsWith('en');
  const name = (x: { name_vi: string; name_en: string }) => (isEn ? x.name_en : x.name_vi);
  return { t, en: isEn, name };
}

/** A field of the editor: its label and help text come from `catalog:editor.field.<name>`. */
export function CatalogField({ name, children, className, group }: { name: FieldName; children: ReactNode; className?: string; group?: boolean }) {
  const { t } = useTranslation('catalog');
  return (
    <Field label={t(`editor.field.${name}.label`)} hint={t(`editor.field.${name}.help`)} className={className} group={group}>
      {children}
    </Field>
  );
}

/** A checkbox field (the label sits next to the box, the help under it). */
export function CatalogCheck({ name, checked, onChange, disabled }: { name: FieldName; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  const { t } = useTranslation('catalog');
  return (
    <div className="grid gap-0.5 text-sm">
      <label className="flex items-center gap-2 font-medium">
        <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
        {t(`editor.field.${name}.label`)}
      </label>
      <span className="pl-6 text-xs text-muted-foreground">{t(`editor.field.${name}.help`)}</span>
    </div>
  );
}

/** Ticks among `options` (a labelled group). */
export function CheckList<T extends string>({ name, options, value, onChange, label }: { name: FieldName; options: readonly T[]; value: readonly T[]; onChange: (v: T[]) => void; label: (v: T) => string }) {
  return (
    <CatalogField name={name} group>
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {options.map((o) => (
          <label key={o} className="flex items-center gap-1.5 text-sm">
            <input type="checkbox" checked={value.includes(o)} onChange={(e) => onChange(e.target.checked ? options.filter((x) => x === o || value.includes(x)) : value.filter((x) => x !== o))} />
            {label(o)}
          </label>
        ))}
      </div>
    </CatalogField>
  );
}

/** A code: typed once on creation, shown read-only afterwards. */
export function CodeInput({ value, onChange, locked, valid }: { value: string; onChange: (v: string) => void; locked: boolean; valid: boolean }) {
  const { t } = useTranslation('catalog');
  return (
    <>
      <Input value={value} onChange={(e) => onChange(e.target.value.toLowerCase().trim())} readOnly={locked} className="font-mono" required aria-invalid={!locked && value !== '' && !valid} />
      {!locked && value !== '' && !valid ? <span className="text-xs text-destructive">{t('editor.codeInvalid')}</span> : null}
    </>
  );
}

/** The two names of everything in the catalogue. */
export function NameFields({ vi, en: enName, setVi, setEn }: { vi: string; en: string; setVi: (v: string) => void; setEn: (v: string) => void }) {
  return (
    <>
      <CatalogField name="nameVi">
        <Input value={vi} onChange={(e) => setVi(e.target.value)} maxLength={120} required />
      </CatalogField>
      <CatalogField name="nameEn">
        <Input value={enName} onChange={(e) => setEn(e.target.value)} maxLength={120} />
      </CatalogField>
    </>
  );
}

/**
 * A code that follows the Vietnamese name (without accents) until the operator types one; fixed
 * once the thing exists.
 */
export function useSuggestedCode(initial: string | undefined, max = 60, letterFirst = false) {
  const [code, setCode] = useState(initial ?? '');
  const [typed, setTyped] = useState(Boolean(initial));
  const fromName = (n: string) => {
    if (typed) return;
    const c = suggestCode(n, max);
    setCode(letterFirst ? c.replace(/^[^a-z]+/, '') : c);
  };
  const type = (v: string) => {
    setTyped(true);
    setCode(v);
  };
  return { code, type, fromName };
}

/** An optional whole number typed in a field (`null`: empty). */
export const optionalInt = (v: string): number | null => (v.trim() === '' ? null : Number(v));

/**
 * The shell of every editor form: its fields, the reason (recorded with the change and in the
 * audit log), the error the server gave and, once saved, "Đã xong".
 */
export function FormShell({
  write,
  onSubmit,
  close,
  disabled,
  submit,
  children,
}: {
  write: { state: { phase: string; error?: unknown }; busy: boolean };
  onSubmit: (reason: string) => void;
  close: () => void;
  disabled?: boolean;
  submit?: string;
  children: ReactNode;
}) {
  const { t } = useTranslation('catalog');
  const [reason, setReason] = useState('');
  const done = write.state.phase === 'done';
  return (
    <form
      className="grid gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(reason.trim());
      }}
    >
      {done ? (
        <Callout tone="ok">{t('applied')}</Callout>
      ) : (
        <>
          {children}
          <ReasonField value={reason} onChange={setReason} />
        </>
      )}
      {write.state.phase === 'error' ? <WriteError error={write.state.error} /> : null}
      <FormFooter busy={write.busy} disabled={disabled || reason.trim() === ''} submit={done ? undefined : (submit ?? t('save'))} onClose={close} />
    </form>
  );
}
